import { FOCUS_RING, Icon } from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { KeyResult } from '@/types/objectives';

import { clampPercent } from '@/utils/objectiveProgress';

interface KeyResultsPanelProps {
    keyResults: KeyResult[];
}

export default function KeyResultsPanel({ keyResults }: KeyResultsPanelProps) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            {/* Full-bleed footer bar of the objective card: small-caps label on the left, a
                chevron that flips when the panel opens on the right. */}
            <button
                type={'button'}
                className={`${FOCUS_RING} flex w-full items-center justify-between border-t border-[#F1F5F9]/70 bg-white/40 px-[32px] py-[16px] text-left text-[11px] font-bold tracking-[0.15em] text-[#94A3B8] uppercase hover:bg-white/60 hover:text-[#475569]`}
                onClick={() => setIsOpen((previous) => !previous)}
                aria-expanded={isOpen}
                data-testid={'key-results-toggle'}
            >
                <span>{t('objectives.card.keyResults')}</span>

                <Icon
                    name={'arrow-arrow-down'}
                    size={'sm'}
                    rotate={isOpen ? 180 : 0}
                    color={'inherit'}
                />
            </button>

            {isOpen && (
                <ul
                    className={'obj-key-results-list border-obj-line flex flex-col gap-6 border-t'}
                    data-testid={'key-results-list'}
                >
                    {keyResults.map((keyResult) => {
                        const percentage = clampPercent(keyResult.percentage);

                        return (
                            <li
                                key={keyResult.id}
                                className={'flex items-center justify-between gap-4'}
                            >
                                <span className={'text-[16px] font-medium text-[#0F172A]'}>
                                    {keyResult.title}
                                </span>

                                <div className={'flex flex-shrink-0 items-center gap-[15px]'}>
                                    <span className={'text-[20px] font-medium text-[#0F172A]'}>
                                        {t('objectives.card.keyResultPercentage', { percentage })}
                                    </span>

                                    <div
                                        className={
                                            'relative h-1.5 w-[146px] overflow-hidden rounded-full bg-[#F1F5F9]'
                                        }
                                        role={'progressbar'}
                                        aria-label={keyResult.title}
                                        aria-valuenow={percentage}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                    >
                                        {/* Purple fading to the vision tint. */}
                                        <div
                                            className={
                                                'from-obj-accent to-obj-vision-tint absolute top-0 left-0 h-full rounded-full bg-gradient-to-r shadow-sm'
                                            }
                                            style={{ width: `${percentage}%` }}
                                        />
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </>
    );
}
